import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Card,
  CardContent,
  CardActions,
  Stack,
  Divider
} from '@mui/material';
import AgentName from './AgentName';
import SystemAlert from '../../common/SystemAlert.js';
import axios from 'axios';
import KnowledgeInfo from '../knowledge/KnowledgeInfo';
import AgentDetail from '../agent/AgentDetail';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ViewListIcon from '@mui/icons-material/ViewList';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InstructionBuilder from '../instructions/InstructionBuilder';
import AgentInstructions from '../agent/AgentInstructions';
import PromptEngineerFlow from '../instructions/promptEngineer/PromptEngineerFlow';
import InstructionChecker, { INSTRUCTION_TEMPLATES } from '../common/InstructionChecker';
import Cookies from '../../common/Cookies.js';
import { materializePromptEngineerGenerationSession } from '../../services/promptEngineerService';

const ONBOARDING_AI_ARCHITECT_RESUME_KEY = 'onboarding.aiArchitect.resume.v1';

const AgentCreation = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [showKnowledgeInfo, setShowKnowledgeInfo] = useState(false);
  const [showAgentInstructions, setShowAgentInstructions] = useState(false);
  const [showAgentDetail, setShowAgentDetail] = useState(false);
  const [kbIdForStep2, setKbIdForStep2] = useState(null);
  const [alert, setAlert] = useState(null);
  const [agent, setAgent] = useState(null);
  const [instructionMode, setInstructionMode] = useState(null); // 'template' | 'wizard' | 'paste' | 'promptEngineer' | 'existingReview'
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [expandedTemplateKey, setExpandedTemplateKey] = useState(null);
  const [instructionSaving, setInstructionSaving] = useState(false);
  const [promptEngineerResumeSnapshot, setPromptEngineerResumeSnapshot] = useState(null);
  const [agentHydrated, setAgentHydrated] = useState(false);
  const [selectedInstructionId, setSelectedInstructionId] = useState(null);
  const [linkedInstruction, setLinkedInstruction] = useState(null);
  const [explicitPromptEngineerReopen, setExplicitPromptEngineerReopen] = useState(false);
  const templateEntries = Object.entries(INSTRUCTION_TEMPLATES);

  const onboardingResumeSnapshot = useMemo(() => {
    if (!promptEngineerResumeSnapshot || typeof promptEngineerResumeSnapshot !== 'object') {
      return null;
    }

    const generationSessionKey = typeof promptEngineerResumeSnapshot.generationSessionKey === 'string'
      ? promptEngineerResumeSnapshot.generationSessionKey.trim()
      : '';

    const stage = promptEngineerResumeSnapshot.stage;
    const currentPhase = Number.isInteger(promptEngineerResumeSnapshot.currentPhase)
      ? promptEngineerResumeSnapshot.currentPhase
      : null;

    if (!generationSessionKey) {
      // Completed interview with no persisted generation session should deterministically
      // restart generation on return instead of restoring a passive completion state.
      if (stage === 'interview' && currentPhase === 6) {
        return {
          ...promptEngineerResumeSnapshot,
          stage: 'progress',
          generationAttempt: 1,
          restartGenerationOnResume: true
        };
      }
      return promptEngineerResumeSnapshot;
    }

    const hasDraft = typeof promptEngineerResumeSnapshot.draftText === 'string'
      && promptEngineerResumeSnapshot.draftText.trim().length > 0;

    if (hasDraft || stage === 'draft' || stage === 'optimizedDraft' || stage === 'progress') {
      return {
        ...promptEngineerResumeSnapshot,
        generationAttempt: Math.max(1, Number(promptEngineerResumeSnapshot.generationAttempt) || 0)
      };
    }

    // A generation session key means interview completion already happened.
    // Force recovery through progress so users are never pushed back into interview steps.
    return {
      ...promptEngineerResumeSnapshot,
      stage: 'progress',
      generationAttempt: Math.max(1, Number(promptEngineerResumeSnapshot.generationAttempt) || 0)
    };
  }, [promptEngineerResumeSnapshot]);

  const loadPromptEngineerResumeSnapshot = () => {
    try {
      const raw = sessionStorage.getItem(ONBOARDING_AI_ARCHITECT_RESUME_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const savePromptEngineerResumeSnapshot = (snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') {
      return;
    }
    setPromptEngineerResumeSnapshot(snapshot);
    try {
      sessionStorage.setItem(ONBOARDING_AI_ARCHITECT_RESUME_KEY, JSON.stringify(snapshot));
    } catch (error) {
      // Ignore storage failures to avoid blocking the interview flow.
    }
  };

  const clearPromptEngineerResumeSnapshot = () => {
    setPromptEngineerResumeSnapshot(null);
    try {
      sessionStorage.removeItem(ONBOARDING_AI_ARCHITECT_RESUME_KEY);
    } catch (error) {
      // Ignore storage failures.
    }
  };

  const formatTemplateTitle = (key) => {
    if (!key) return '';
    const withSpaces = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/[-_]/g, ' ')
      .trim();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const resetInstructionCreationState = () => {
    setInstructionMode(null);
    setSelectedTemplateKey(null);
    setTemplateName('');
    setTemplateContent('');
    setManualName('');
    setManualContent('');
    setExpandedTemplateKey(null);
    setExplicitPromptEngineerReopen(false);
  };

  useEffect(() => {
    const snapshot = loadPromptEngineerResumeSnapshot();
    if (snapshot) {
      setPromptEngineerResumeSnapshot(snapshot);
    }
  }, []);

  useEffect(() => {
    if (!agentHydrated || !onboardingResumeSnapshot) {
      return;
    }

    // Preserve partial interview resume for first-time setup only.
    if (agent?.instructionId) {
      return;
    }

    let cancelled = false;

    const openPromptEngineerResume = () => {
      if (cancelled) return;
      setCurrentStep(3);
      setShowForm(false);
      setShowKnowledgeInfo(false);
      setShowAgentInstructions(true);
      setShowAgentDetail(false);
      setInstructionMode('promptEngineer');
      setExplicitPromptEngineerReopen(false);
    };

    const recoverOnboardingGeneration = async () => {
      const generationSessionKey = typeof onboardingResumeSnapshot?.generationSessionKey === 'string'
        ? onboardingResumeSnapshot.generationSessionKey.trim()
        : '';

      if (!generationSessionKey) {
        openPromptEngineerResume();
        return;
      }

      try {
        const materialized = await materializePromptEngineerGenerationSession(generationSessionKey, {
          createdBy: Cookies.getUserName?.() || 'system',
          updatedBy: Cookies.getUserName?.() || 'system',
          instructionId: null
        });

        if (cancelled) return;

        const createdInstruction = materialized?.instruction || {};
        const recoveredInstructionId = createdInstruction.id || createdInstruction.instructionId;

        if (recoveredInstructionId) {
          await axios.post('/instructions/transitionLifecycle', {
            id: recoveredInstructionId,
            lifecycleState: 'READY',
            reason: 'Recovered completed onboarding generation before agent attachment'
          });

          await axios.post('/agents/updateInstruction', {
            agentId: agent?.id,
            instructionId: recoveredInstructionId
          });

          if (cancelled) return;

          setAgent((prev) => prev ? { ...prev, instructionId: recoveredInstructionId } : prev);
          setSelectedInstructionId(recoveredInstructionId);
          setCurrentStep(3);
          setShowForm(false);
          setShowKnowledgeInfo(false);
          setShowAgentInstructions(true);
          setShowAgentDetail(false);
          setInstructionMode('existingReview');
          setExplicitPromptEngineerReopen(false);
          clearPromptEngineerResumeSnapshot();
          return;
        }
      } catch (error) {
        // Fall through to in-flow recovery when materialization is not yet available.
      }

      openPromptEngineerResume();
    };

    recoverOnboardingGeneration();

    return () => {
      cancelled = true;
    };
  }, [agentHydrated, onboardingResumeSnapshot, agent?.instructionId, agent?.id]);

  useEffect(() => {
    if (!agent?.instructionId) {
      setSelectedInstructionId(null);
      setLinkedInstruction(null);
      return;
    }

    setSelectedInstructionId(agent.instructionId);
    let cancelled = false;
    axios.post(`/instructions/get?id=${agent.instructionId}`)
      .then((response) => {
        if (cancelled) return;
        setLinkedInstruction(response.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setLinkedInstruction(null);
      });

    return () => {
      cancelled = true;
    };
  }, [agent?.instructionId]);

  const resolveInstructionModeForStep3 = () => {
    if (agent?.instructionId) {
      return 'existingReview';
    }
    if (onboardingResumeSnapshot) {
      return 'promptEngineer';
    }
    return null;
  };

  const handleTemplateSelect = (key) => {
    setSelectedTemplateKey(key);
    setTemplateName(formatTemplateTitle(key));
    setTemplateContent(INSTRUCTION_TEMPLATES[key] || '');
  };

  const resolveErrorMessage = (error, fallbackMessage) => {
    return error?.response?.data?.message
      || error?.response?.data?.error
      || error?.message
      || fallbackMessage;
  };

  const transitionInstructionToReady = async (instructionId) => {
    if (!instructionId) return;
    await axios.post('/instructions/transitionLifecycle', {
      id: instructionId,
      lifecycleState: 'READY',
      reason: 'Explicit onboarding readiness transition before agent attachment'
    });
  };

  const attachInstructionToAgent = async (instructionId) => {
    if (!agent?.id || !instructionId) return;
    await axios.post('/agents/updateInstruction', {
      agentId: agent.id,
      instructionId
    });
  };

  const handleInstructionSaved = (instructionId) => {
    if (!instructionId) {
      setAlert({ severity: 'error', message: 'Instruction saved but missing identifier.' });
      setTimeout(() => setAlert(null), 3000);
      return;
    }
    setAlert({ severity: 'success', message: 'Instruction Saved Successfully!' });
    const updatedAgent = {
      ...agent,
      instructionId
    };
    setAgent(updatedAgent);
    setSelectedInstructionId(instructionId);
    setSteps(prevSteps => {
      const updatedSteps = prevSteps.map(step =>
        step.id === 3 ? { ...step, completed: true } : step
      );
      const newCurrentStep = determineCurrentStep(updatedSteps);
      setCurrentStep(newCurrentStep);
      return updatedSteps;
    });
    setShowAgentInstructions(false);
    clearPromptEngineerResumeSnapshot();
    resetInstructionCreationState();
    setTimeout(() => setAlert(null), 3000);
  };

  const saveInstruction = async (instruction) => {
    const trimmedName = (instruction?.name || '').trim();
    const trimmedContent = (instruction?.content || '').trim();
    if (!trimmedName || !trimmedContent) return;
    try {
      setInstructionSaving(true);

      const generationSessionKey = instruction?.promptEngineerMeta?.generationSessionKey;
      if (instruction?.sourceType === 'ai_prompt_engineer' && generationSessionKey) {
        const existingInstructionId = instruction?.id || linkedInstruction?.id || selectedInstructionId || null;
        const materialized = await materializePromptEngineerGenerationSession(generationSessionKey, {
          createdBy: Cookies.getUserName?.() || 'system',
          updatedBy: Cookies.getUserName?.() || 'system',
          instructionId: existingInstructionId
        });

        const createdInstruction = materialized?.instruction || {};
        const newInstructionId = createdInstruction.id || createdInstruction.instructionId;

        if (newInstructionId) {
          await transitionInstructionToReady(newInstructionId);
          await attachInstructionToAgent(newInstructionId);
        }

        handleInstructionSaved(newInstructionId);
        return;
      }

      const allInstructionsResponse = await axios.post('/instructions/getAll');
      const allInstructions = allInstructionsResponse.data || [];
      const duplicate = allInstructions.some(instr =>
        instr.name?.toLowerCase().trim() === trimmedName.toLowerCase()
      );
      if (duplicate) {
        setAlert({ severity: 'error', message: 'An instruction with this name already exists. Please choose a different name.' });
        setInstructionSaving(false);
        setTimeout(() => setAlert(null), 3000);
        return;
      }

      const { id, ...newInstruction } = {
        ...instruction,
        name: trimmedName,
        content: trimmedContent
      };
      const createResponse = await axios.post('/instructions/create', newInstruction);
      const createdInstruction = createResponse.data || {};
      const newInstructionId = createdInstruction.id || createdInstruction.instructionId;

      if (newInstructionId) {
        await transitionInstructionToReady(newInstructionId);
        await attachInstructionToAgent(newInstructionId);
      }

      handleInstructionSaved(newInstructionId);
    } catch (err) {
      console.error('Error saving instruction:', err);
      setAlert({ severity: 'error', message: resolveErrorMessage(err, 'Failed to save instruction. Please try again.') });
      setTimeout(() => setAlert(null), 3000);
    } finally {
      setInstructionSaving(false);
    }
  };

  // Check if any step is currently active (showing a form)
  const isAnyStepActive = showForm || showKnowledgeInfo || showAgentInstructions || showAgentDetail;

  const [steps, setSteps] = useState([
    {
      id: 1,
      header: 'STEP 1:',
      icon: BadgeIcon,
      title: 'Name Your Agent',
      description: 'Name your agent and designate a sales or customer service function.',
      completed: false
    },
    {
      id: 2,
      header: 'STEP 2:',
      icon: SchoolIcon,
      title: 'Train Your Agent',
      description: 'Upload your training documents, spreadsheets, and web pages from which your agent can learn.',
      completed: false
    },
    {
      id: 3,
      header: 'STEP 3:',
      icon: ListAltIcon,
      title: 'Instruct Your Agent',
      description: 'Create or use templated instructions to define how your agent, speaks, and acts.',
      completed: false
    },
    {
      id: 4,
      header: 'STEP 4:',
      icon: SmartToyIcon,
      title: 'Configure Your Agent',
      description: 'From chat windows to agent tools, configure your agent for deployment.',
      completed: false
    }
  ]);

  // Function to determine current step based on completed steps
  const determineCurrentStep = (stepsData) => {
    // Find the first incomplete step
    const firstIncompleteStep = stepsData.find(step => !step.completed);
    if (firstIncompleteStep) {
      return firstIncompleteStep.id;
    }
    // If all steps are completed, return the last step
    return stepsData[stepsData.length - 1].id;
  };

  // Load agent data on component mount
  useEffect(() => {
    const loadAgent = async () => {
      try {
        const response = await axios.post('/agents/getFirstAgent');
        const agentData = response.data;

        if (agentData) {
          setAgent(agentData);
          console.log(agentData);
          // Update step completion status based on agent data
          const updatedSteps = steps.map(step => {
            switch (step.id) {
              case 1:
                // Step 1 is complete if agent exists and has an ID
                return { ...step, completed: agentData.id != null };
              case 2:
                // Step 2 is complete if agent has KBs and count > 0
                return { ...step, completed: agentData.kbs != null && agentData.kbs.length > 0 };
              case 3:
                // Step 3 is complete if agent has instructions
                return { ...step, completed: agentData.instructionId != null && agentData.instructionId != 0 };
              case 4:
                // Step 4 is complete if agent has customization
                return { ...step, completed: agentData.customization != null };
              default:
                return step;
            }
          });
          setSteps(updatedSteps);

          // Automatically set current step to the first incomplete step
          const newCurrentStep = determineCurrentStep(updatedSteps);
          setCurrentStep(newCurrentStep);
        }
      } catch (error) {
        console.error('Error loading agent:', error);
        // If no agent exists or error occurs, steps remain incomplete
        // Set current step to 1 (first step)
        setCurrentStep(1);
      } finally {
        setAgentHydrated(true);
      }
    };

    loadAgent();
  }, []); // Add steps as dependency since we're using it inside

  // Check for step4Completed parameter from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const step4Completed = urlParams.get('step4Completed');

    if (step4Completed === 'true') {
      // Mark step 4 as completed
      const updatedSteps = steps.map(step =>
        step.id === 4 ? { ...step, completed: true } : step
      );
      setSteps(updatedSteps);

      // Automatically advance to the next step (step 4 is the last step, so stay on step 4)
      const newCurrentStep = determineCurrentStep(updatedSteps);
      setCurrentStep(newCurrentStep);

      // Clear the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Show success alert
      setAlert({ severity: 'success', message: 'Agent customization saved successfully! Step 4 completed.' });
      setTimeout(() => setAlert(null), 3000);
    }
  }, [steps]);

  const handleStepClick = (stepId) => {
    // First step is always clickable
    if (stepId === 1) {
      setCurrentStep(stepId);
      setShowForm(true);
      setShowKnowledgeInfo(false);
      setShowAgentInstructions(false);
      setShowAgentDetail(false);

      // Scroll to bottom of page after a short delay to ensure form is rendered
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);

      return;
    }

    // Other steps are only clickable if the previous step is completed
    const previousStep = steps.find(step => step.id === stepId - 1);
    if (previousStep && previousStep.completed) {
      setCurrentStep(stepId);
      setShowForm(false);

      // Show KnowledgeInfo for step 2
      if (stepId === 2) {
        setShowKnowledgeInfo(true);
        setShowAgentInstructions(false);
        setShowAgentDetail(false);

        // Determine which KB to show: use agent-linked KB if exists, otherwise null for new KB
        (async () => {
          try {
            if (agent?.kbs && agent.kbs.length > 0) {
              console.log('Agent has KBs:', agent.kbs);
              console.log('Using first KB ID:', agent.kbs[0]);
              setKbIdForStep2(agent.kbs[0].id); // Show existing KB attached to agent
            } else {
              console.log('Agent has no KBs, will create new KB');
              setKbIdForStep2(null); // Show empty form for creating new KB
            }
          } catch (e) {
            console.error('Error checking agent KBs:', e);
            setKbIdForStep2(null); // Default to creating new KB
          }
        })();

        // Scroll to bottom of page after a short delay to ensure form is rendered
        setTimeout(() => {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      } else if (stepId === 3) {
        // Show AgentInstructions for step 3
        setShowKnowledgeInfo(false);
        setShowAgentInstructions(true);
        setShowAgentDetail(false);
        const resolvedMode = resolveInstructionModeForStep3();
        setInstructionMode(resolvedMode);
        setExplicitPromptEngineerReopen(false);
        if (resolvedMode === 'existingReview' && agent?.instructionId) {
          setSelectedInstructionId(agent.instructionId);
        }
        if (resolvedMode !== 'promptEngineer') {
          setPromptEngineerResumeSnapshot(null);
        }

        // Scroll to bottom of page after a short delay to ensure form is rendered
        setTimeout(() => {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      } else if (stepId === 4) {
        // Show AgentDetail for step 4
        setShowKnowledgeInfo(false);
        setShowAgentInstructions(false);
        setShowAgentDetail(true);

        // Scroll to bottom of page after a short delay to ensure form is rendered
        setTimeout(() => {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      } else {
        setShowKnowledgeInfo(false);
        setShowAgentInstructions(false);
        setShowAgentDetail(false);
      }
    }
  };

  const onCustomizationSaved = (customization) => {
    console.log('Agent customization saved successfully! Step 4 completed.');
    console.log('customization', customization);
    const updatedAgent = {
      ...agent,
      customization: customization
    };
    //    setStep4Completed(true);
    const updatedSteps = steps.map(step =>
      step.id === 4 ? { ...step, completed: true } : step
    );
    setSteps(updatedSteps);
    setAgent(updatedAgent);

    // Automatically advance to the next step (step 4 is the last step, so stay on step 4)
    const newCurrentStep = determineCurrentStep(updatedSteps);
    setCurrentStep(newCurrentStep);

    Cookies.setCookies('hasAgent', true);
  }

  return (
    <Box sx={{
      width: '100%',
      p: 4,
      textAlign: isAnyStepActive ? 'left' : 'center'
    }}>
      {/* System Alert for success/error messages */}
      {alert && (
        <SystemAlert open={!!alert} severity={alert.severity} message={alert.message} onClose={() => setAlert(null)} />
      )}
      {/* Main Title - Only show when no step is active */}
      {!isAnyStepActive && (
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 'bold',
            color: '#2c3e50',
            mb: 3,
            fontSize: '2.5rem'
          }}
        >
          Create Your First Agent
        </Typography>
      )}

      {/* Introductory Text */}
      {!isAnyStepActive && (
        <Typography
          variant="body1"
          sx={{
            color: '#5a6c7d',
            mb: 6,
            maxWidth: 800,
            mx: 'auto',
            lineHeight: 1.6
          }}
        >
          Follow these steps to create your first AI agent. Before you begin, envision what role your agent will play
          (e.g., answering questions, updating CRM, scheduling appointments) and prepare your training data
          (spreadsheets, documents, web pages).
        </Typography>
      )}

      {/* Steps Display - Show Stepper when any step is active, otherwise show step boxes */}
      {isAnyStepActive ? (
        // Stepper when any step is active
        <Box sx={{ width: '100%' }}>
          <Stepper activeStep={currentStep - 1} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.id} completed={step.completed}>
                <StepButton
                  onClick={() => handleStepClick(step.id)}
                  disabled={!(step.id === 1 || (step.id > 1 && steps.find(s => s.id === step.id - 1)?.completed))}
                  sx={{
                    // Highlight the active step
                    ...(currentStep === step.id && {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      borderRadius: 1,
                      border: '2px solid #1565c0', // Dark blue border for current step
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                      }
                    })
                  }}
                >
                  <StepLabel
                    sx={{
                      // Make active step text more prominent
                      ...(currentStep === step.id && {
                        fontWeight: 'bold',
                        color: '#1976d2'
                      })
                    }}
                  >
                    {step.title}
                  </StepLabel>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      ) : (
        // Original step boxes when no step is active
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', width: '100%' }}>
          {steps.map((step) => {
            const IconComponent = step.icon;
            return (
              <Box sx={{ flex: '1 1 300px', minWidth: 300 }} key={step.id}>
                <Paper
                  elevation={2}
                  onClick={() => handleStepClick(step.id)}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    backgroundColor: '#f8f9fa',
                    border: currentStep === step.id ? '2px solid #1565c0' : '1px solid #e9ecef', // Dark blue border for current step
                    height: '100%',
                    minHeight: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    cursor: (step.id === 1 || (step.id > 1 && steps.find(s => s.id === step.id - 1)?.completed)) ? 'pointer' : 'not-allowed',
                    opacity: (step.id === 1 || (step.id > 1 && steps.find(s => s.id === step.id - 1)?.completed)) ? 1 : 0.6,
                    '&:hover': {
                      transform: (step.id === 1 || (step.id > 1 && steps.find(s => s.id === step.id - 1)?.completed)) ? 'translateY(-2px)' : 'none',
                      boxShadow: (step.id === 1 || (step.id > 1 && steps.find(s => s.id === step.id - 1)?.completed)) ? 4 : 2
                    }
                  }}
                >
                  {/* Step Header */}
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#6c757d',
                      fontWeight: 'bold',
                      mb: 1,
                      fontSize: '1.1rem'
                    }}
                  >
                    {step.header}
                  </Typography>

                  {/* Step Title with Icon and Title Centered on Separate Lines */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <IconComponent
                      sx={{
                        fontSize: 32,
                        color: '#3498db',
                        mb: 1
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        textAlign: 'center'
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Box>

                  {/* Step Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.5,
                      flexGrow: 1
                    }}
                  >
                    {step.description}
                  </Typography>

                  {/* Status Chip at Bottom Center */}
                  {(step.completed || (currentStep === step.id && !step.completed)) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Chip
                        label={step.completed ? "Completed" : "Current"}
                        color={step.completed ? "success" : "primary"}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: '24px',
                          px: 2
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Box>
            );
          })}
        </Box>
      )}


      {/* Agent Name Form */}
      {showForm && (
        <Box sx={{ mt: 4, p: 4, backgroundColor: '#f8f9fa', borderRadius: 3, border: '1px solid #e9ecef', width: '100%' }}>
          {/* <Typography variant="h6" sx={{ mb: 3, color: '#2c3e50' }}>
            Step 1: Name Your Agent
          </Typography> */}
          <AgentName
            agentId={agent?.id || null}
            agentData={agent}
            onAgentCreated={(newAgentId, agentData) => {
              setAgent(agentData);
              setAlert({ severity: 'success', message: 'Agent Created Successfully!' });

              // Mark step 1 as completed
              const updatedSteps = steps.map(step =>
                step.id === 1 ? { ...step, completed: true } : step
              );
              setSteps(updatedSteps);
              setShowForm(false);

              // Automatically advance to the next step (step 2)
              const newCurrentStep = determineCurrentStep(updatedSteps);
              setCurrentStep(newCurrentStep);

              // Clear alert after 3 seconds
              setTimeout(() => setAlert(null), 3000);
            }}
            onCancel={() => setShowForm(false)}
          />
        </Box>
      )}

      {/* Knowledge - Show existing KB in form (no separate list) */}
      {showKnowledgeInfo && (
        <Box sx={{ mt: 1, p: 1, backgroundColor: '#f8f9fa', borderRadius: 3, border: '1px solid #e9ecef', width: '100%' }}>
          {/* <Typography variant="h6" sx={{ mb: 3, color: '#2c3e50' }}>
            Step 2: Train Your Agent
          </Typography> */}
          <KnowledgeInfo
            isEmbedded={true}
            onStepCompleted={async (kbId) => {
              try {
                // Save agent with the new knowledge base
                const response = await axios.post('/agents/saveAgentKb', null, {
                  params: {
                    agentId: agent?.id,
                    kbId: kbId
                  }
                });

                if (response.data && response.data.success) {
                  setAlert({ severity: 'success', message: 'Knowledge Base Created and Linked to Agent Successfully!' });

                  // Add kbId to agent's kbs array
                  const updatedAgent = {
                    ...agent,
                    kbs: agent.kbs ? [...agent.kbs, { id: kbId }] : [{ id: kbId }]
                  };
                  setAgent(updatedAgent);

                  // Mark step 2 as completed
                  const updatedSteps = steps.map(step =>
                    step.id === 2 ? { ...step, completed: true } : step
                  );
                  setSteps(updatedSteps);
                  setShowKnowledgeInfo(false);

                  // Automatically advance to the next step (step 3)
                  const newCurrentStep = determineCurrentStep(updatedSteps);
                  setCurrentStep(newCurrentStep);

                  // Clear alert after 3 seconds
                  setTimeout(() => setAlert(null), 3000);
                } else {
                  setAlert({ severity: 'error', message: 'Failed to link knowledge base to agent. Please try again.' });
                  setTimeout(() => setAlert(null), 3000);
                }
              } catch (error) {
                console.error('Error linking knowledge base to agent:', error);
                const d = error?.response?.data;
                const apiMsg = typeof d === 'string' ? d : d?.message;
                setAlert({
                  severity: 'error',
                  message: apiMsg || 'Failed to link knowledge base to agent. Please try again.'
                });
                setTimeout(() => setAlert(null), 3000);
              }
            }}
            onCancel={() => setShowKnowledgeInfo(false)}
            kbIdFromWizard={kbIdForStep2}
          />
        </Box>
      )}

      {/* Agent Instructions */}
      {showAgentInstructions && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: '#f8f9fa',
            borderRadius: 3,
            border: '1px solid #e9ecef',
            width: '100%'
          }}
        >
          {instructionMode === null && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                How would you like to create instructions?
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))'
                  },
                  gap: 2,
                  alignItems: 'stretch',
                  width: '100%',
                  maxWidth: '1500px',
                  mr: 'auto'
                }}
              >
                {/* Meet Your AI Instruction Architect */}
                <Card
                  elevation={3}
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    minHeight: 190,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <SmartToyIcon color="primary" />

                      <Typography variant="h6">
                        Meet Your AI Instruction Architect
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        minHeight: { xs: 'auto', lg: '3em' },
                        maxWidth: '470px',
                        lineHeight: 1.5,
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word'
                      }}
                    >
                      Answer a few business questions and the AI Instruction Architect will engineer
                      production-ready instructions for you.
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setExplicitPromptEngineerReopen(false);
                        setInstructionMode('promptEngineer');
                      }}
                      size="small"
                    >
                      START WITH THE AI INSTRUCTION ARCHITECT
                    </Button>
                  </CardActions>
                </Card>

                {/*
                Templates
                <Card
                  elevation={3}
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    minHeight: 190,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <AutoAwesomeIcon color="primary" />

                      <Typography variant="h6">
                        Use existing templates
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        minHeight: { xs: 'auto', lg: '3em' },
                        maxWidth: '470px',
                        lineHeight: 1.5,
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word'
                      }}
                    >
                      Browse curated starter instructions and customize them to fit
                      your needs.
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setInstructionMode('template')}
                      size="small"
                    >
                      BROWSE TEMPLATES
                    </Button>
                  </CardActions>
                </Card>
                */}

                {/*
          Future Wizard Flow

          <Card
            elevation={3}
            sx={{
              width: '100%',
              minWidth: 0,
              minHeight: 190,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <ViewListIcon color="primary" />

                <Typography variant="h6">
                  Use wizard to create
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  minHeight: { xs: 'auto', lg: '3em' },
                  maxWidth: '470px',
                  lineHeight: 1.5,
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word'
                }}
              >
                Step through the guided builder to generate tailored
                instructions.
              </Typography>
            </CardContent>

            <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
              <Button
                variant="contained"
                onClick={() => setInstructionMode('wizard')}
                size="small"
              >
                START WIZARD
              </Button>
            </CardActions>
          </Card>
          */}

                {/* Paste Existing Instructions */}
                <Card
                  elevation={3}
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    minHeight: 190,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <ContentPasteGoIcon color="primary" />

                      <Typography variant="h6">
                        I already have instructions
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        minHeight: { xs: 'auto', lg: '3em' },
                        maxWidth: '470px',
                        lineHeight: 1.5,
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word'
                      }}
                    >
                      Paste your existing instructions or write them in your own
                      words.
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setInstructionMode('paste')}
                      size="small"
                    >
                      PASTE INSTRUCTIONS
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            </Box>
          )}

          {instructionMode === 'template' && selectedTemplateKey === null && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Select a template to start from</Typography>
                <Button
                  startIcon={<ArrowBackIcon />}
                  variant="text"
                  size="small"
                  onClick={() => {
                    resetInstructionCreationState();
                    setInstructionMode(null);
                  }}
                >
                  Back
                </Button>
              </Stack>
              <Stack spacing={1}>
                {templateEntries.map(([key, content]) => (
                  <Accordion
                    key={key}
                    elevation={1}
                    expanded={expandedTemplateKey === key}
                    onChange={() => setExpandedTemplateKey(expandedTemplateKey === key ? null : key)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>{formatTemplateTitle(key)}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                          {content}
                        </Typography>
                        <Button variant="contained" onClick={() => handleTemplateSelect(key)} size="small" disabled={instructionSaving}>
                          Use Template
                        </Button>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          )}

          {instructionMode === 'template' && selectedTemplateKey !== null && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Customize Template</Typography>
                <Button
                  startIcon={<ArrowBackIcon />}
                  variant="text"
                  size="small"
                  onClick={() => {
                    setSelectedTemplateKey(null);
                    setTemplateName('');
                    setTemplateContent('');
                  }}
                >
                  Back to templates
                </Button>
              </Stack>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Instruction Name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Instruction Content"
                      value={templateContent}
                      onChange={(e) => setTemplateContent(e.target.value)}
                      minRows={16}
                      multiline
                      fullWidth
                    />
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => saveInstruction({ name: templateName, content: templateContent })}
                        disabled={instructionSaving || !templateName.trim() || !templateContent.trim()}
                      >
                        {instructionSaving ? 'Saving…' : 'Save Instruction'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedTemplateKey(null);
                          setTemplateName('');
                          setTemplateContent('');
                        }}
                        disabled={instructionSaving}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    width: { xs: '100%', lg: 360 },
                    flexShrink: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2
                  }}
                >
                  <InstructionChecker
                    content={templateContent}
                    onTemplateInsert={(template) => {
                      setTemplateContent(template);
                      if (!templateName?.trim()) {
                        setTemplateName('Imported Instruction');
                      }
                    }}
                    width="100%"
                  />
                </Box>
              </Box>
            </Box>
          )}

          {instructionMode === 'promptEngineer' && (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Meet Your AI Instruction Architect</Typography>
                <Button
                  startIcon={<ArrowBackIcon />}
                  variant="text"
                  size="small"
                  onClick={() => {
                    if (explicitPromptEngineerReopen) {
                      setInstructionMode('existingReview');
                      setExplicitPromptEngineerReopen(false);
                      return;
                    }

                    resetInstructionCreationState();
                    setInstructionMode(null);
                  }}
                  disabled={instructionSaving}
                >
                  Back
                </Button>
              </Stack>
              <PromptEngineerFlow
                onExit={() => {
                  if (explicitPromptEngineerReopen) {
                    setInstructionMode('existingReview');
                    setExplicitPromptEngineerReopen(false);
                    return;
                  }

                  resetInstructionCreationState();
                  setInstructionMode(null);
                  clearPromptEngineerResumeSnapshot();
                }}
                onSaveInstruction={saveInstruction}
                isSaving={instructionSaving}
                onUnsavedChange={() => { }}
                initialConfig={explicitPromptEngineerReopen ? (linkedInstruction?.promptEngineerConfig || null) : null}
                initialInstructionName={explicitPromptEngineerReopen ? (linkedInstruction?.name || '') : ''}
                initialDraftText={explicitPromptEngineerReopen ? (linkedInstruction?.content || '') : ''}
                existingInstructionId={explicitPromptEngineerReopen ? (linkedInstruction?.id || selectedInstructionId || null) : null}
                reopenMode={explicitPromptEngineerReopen}
                resumeSnapshot={explicitPromptEngineerReopen ? null : onboardingResumeSnapshot}
                onboardingRecoveryMode={!explicitPromptEngineerReopen}
                onResumeSnapshotChange={savePromptEngineerResumeSnapshot}
                onResumeSnapshotClear={clearPromptEngineerResumeSnapshot}
              />
            </Box>
          )}

          {instructionMode === 'existingReview' && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Review Saved Instructions</Typography>
              </Stack>

              <AgentInstructions
                agentId={agent?.id}
                selectedInstructionId={selectedInstructionId}
                setSelectedInstructionId={setSelectedInstructionId}
                onInstructionChange={setSelectedInstructionId}
                onSaveOrCreate={handleInstructionSaved}
                onClose={() => setShowAgentInstructions(false)}
                showActions={true}
                hideSelect={false}
                isEmbedded={true}
              />
            </Box>
          )}

          {instructionMode === 'paste' && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Paste instructions</Typography>
                <Button
                  startIcon={<ArrowBackIcon />}
                  variant="text"
                  size="small"
                  onClick={() => {
                    resetInstructionCreationState();
                    setInstructionMode(null);
                  }}
                >
                  Back
                </Button>
              </Stack>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Instruction Name"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Instruction Content"
                      value={manualContent}
                      onChange={(e) => setManualContent(e.target.value)}
                      minRows={16}
                      multiline
                      fullWidth
                      placeholder="Paste your instructions here…"
                    />
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => saveInstruction({ name: manualName, content: manualContent })}
                        disabled={instructionSaving || !manualName.trim() || !manualContent.trim()}
                      >
                        {instructionSaving ? 'Saving…' : 'Save Instruction'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          resetInstructionCreationState();
                          setInstructionMode(null);
                        }}
                        disabled={instructionSaving}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    width: { xs: '100%', lg: 360 },
                    flexShrink: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2
                  }}
                >
                  <InstructionChecker
                    content={manualContent}
                    onTemplateInsert={(template) => {
                      setManualContent(template);
                      if (!manualName?.trim()) {
                        setManualName('Imported Instruction');
                      }
                    }}
                    width="100%"
                  />
                </Box>
              </Box>
            </Box>
          )}

          {instructionMode === 'wizard' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6">Instruction Builder Wizard</Typography>
                <Button
                  startIcon={<ArrowBackIcon />}
                  variant="text"
                  size="small"
                  onClick={() => {
                    resetInstructionCreationState();
                    setInstructionMode(null);
                  }}
                  disabled={instructionSaving}
                >
                  Back
                </Button>
              </Stack>
              <Divider />
              <InstructionBuilder onComplete={saveInstruction} isSaving={instructionSaving} />
            </Box>
          )}

        </Box>
      )}

      {/* Agent Detail Form */}
      {showAgentDetail && (
        <Box sx={{ mt: 4, p: 4, backgroundColor: '#f8f9fa', borderRadius: 3, border: '1px solid #e9ecef', width: '100%' }}>
          {/* <Typography variant="h6" sx={{ mb: 3, color: '#2c3e50' }}>
            Step 4: Configure Your Agent
          </Typography> */}
          <AgentDetail agentIdFromWizard={agent?.id} onCustomizationSaved={onCustomizationSaved} onCancel={() => setShowAgentDetail(false)} />
        </Box>
      )}
    </Box>
  );
};

export default AgentCreation; 